import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import fetchData from "../utils/fetchData";
import { notifyError } from "../utils/notify";
import useFetchDebounce from "./useFetchDebounce";
import useFetchDebounceECDH from "./useFetchDebounceECDH";
import useHookConsultStatus from "../apps/UpdateCommerce/hook/useHookConsultStatus";
import {
  Cache_Remove_StatusUpdateDatetimeoldPeticion,
  Cache_Remove_StatusUpdateDatetimeoldShowmodal,
} from "../apps/UpdateCommerce/cache/cacheKeys";
import { jwtDecode } from "jwt-decode";

const urlLog = `${process.env.REACT_APP_URL_SERVICE_COMMERCE}/login`;
const urlQuota = `${process.env.REACT_APP_URL_SERVICE_COMMERCE}/cupo-v2`;
const urlCiudad_dane = `${process.env.REACT_APP_URL_DANE_MUNICIPIOS}`;
const urlInfoTicket = `${process.env.REACT_APP_URL_TRXS_TRX}/transaciones`;
const url_iam_pdp_users = process.env.REACT_APP_URL_IAM_PDP;
const url_user = process.env.REACT_APP_URL_COGNITO;
const public_urls = process.env.REACT_APP_URL_SERVICE_PUBLIC;
const url_pdp_commerce = process.env.REACT_APP_URL_SERVICE_COMMERCE;

const validateUser = async (email) => {
  const get = {
    email: email,
  };
  if (!email) {
    throw new Error("Sin datos de busqueda", {
      cause: "custom",
    });
  }

  try {
    const res = await fetchData(url_user, "GET", get, {}, {}, false);
    if (!res?.Status) {
      throw new Error("Usuario invalido", {
        cause: "custom",
      });
    }
    return res;
  } catch (err) {
    throw err;
  }
};

const infoTicket = async (id_trx, Tipo_operacion, ticket) => {
  const get = {
    id_trx: id_trx,
    Tipo_operacion: Tipo_operacion,
  };
  const post = { Ticket: ticket };

  try {
    const res = await fetchData(urlInfoTicket, "PUT", get, post);
    return res;
  } catch (err) { }
};

const fetchDane = async (codigo_dane) => {
  if (!codigo_dane) {
    return "";
  }
  try {
    const resp_ciudad = await fetchData(
      urlCiudad_dane,
      "GET",
      {
        c_digo_dane_del_municipio: codigo_dane,
      },
      {},
      {},
      false
    );
    return resp_ciudad[0].municipio;
  } catch (err) { }
};

const initialUser = {
  isSignedIn: false,
  cognitoUser: null,
  userInfo: null,
  roleInfo: null,
  quotaInfo: null,
  pdpUser: null,
  userPermissions: [],
  commerceInfo: null,
  detail_status_update: undefined,
};

const SIGN_IN = "SIGN_IN";
const CONFIRM_SIGN_IN = "CONFIRM_SIGN_IN";
const SIGN_OUT = "SIGN_OUT";
const SET_USERINFO = "SET_USERINFO";
const SET_ROLEINFO = "SET_ROLEINFO";
const SET_PERMISSIONS = "SET_PERMISSIONS";
const SET_PDPUSER = "SET_PDPUSER";
const SET_QUOTA = "SET_QUOTA";
const SET_QUOTA_COMISION = "SET_QUOTA_COMISION";
const SET_COMMERCE_INFO = "SET_COMMERCE_INFO";
const SET_DETAIL_STATUS_UPDATE = "SET_DETAIL_STATUS_UPDATE";

const reducerAuth = (userState, action) => {
  const { payload } = action;
  switch (action.type) {
    case SIGN_IN:
      const { user } = payload;
      return { ...userState, cognitoUser: user };

    case SIGN_OUT:
      return initialUser;

    case SET_USERINFO:
      const { uInfo } = payload;
      return { ...userState, userInfo: uInfo };

    case SET_ROLEINFO:
      const { roleInfo: role } = userState;
      return { ...userState, roleInfo: { ...role, ...payload } };

    case SET_PERMISSIONS:
      const { uAccess } = payload;
      return { ...userState, userPermissions: uAccess };

    case SET_PDPUSER:
      const { pdpU } = payload;
      return { ...userState, pdpUser: pdpU };

    case SET_QUOTA:
      const { quota } = payload;
      return { ...userState, quotaInfo: quota };

    case SET_QUOTA_COMISION:
      const comm = payload;
      return {
        ...userState,
        quotaInfo: { ...userState.quotaInfo, comision: comm },
      };

    case SET_COMMERCE_INFO:
      const { commerce } = payload;
      return { ...userState, commerceInfo: commerce };

    case CONFIRM_SIGN_IN:
      const { loggedUser } = payload;
      if (!loggedUser) {
        return initialUser;
      }
      return { ...userState, isSignedIn: true };

    case SET_DETAIL_STATUS_UPDATE:
      const { detail_status_update } = payload;
      return { ...userState, detail_status_update: detail_status_update };

    default:
      throw new Error(`Bad action ${JSON.stringify(action, null, 2)}`);
  }
};

export const AuthContext = createContext({
  signIn: () => { },
  confirmSignIn: () => { },
  signOut: () => { },
  infoTicket,
  updateCommerceQuota: () => { },
  parameters: null,
  qr: null,
  ...initialUser,
  showModalPublicidad: false,
  setShowModalPublicidad: () => { },
  notLoadedPermissions: true,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const useProvideAuth = () => {
  const [qr, setQr] = useState("");

  const [parameters, setParameters] = useState("");
  const [, setSuserInactive] = useState("");

  const [timer, setTimer] = useState(null);
  const [showModalPublicidad, setShowModalPublicidad] = useState(false);
  const [notLoadedPermissions, setNotLoadedPermissions] = useState(true);

  const [userState, dispatchAuth] = useReducer(reducerAuth, initialUser);

  const { roleInfo, pdpUser } = userState;
  const id_comercio = roleInfo?.id_comercio;
  const id_dispositivo = roleInfo?.id_dispositivo;
  const [eventStatusUpdate] = useHookConsultStatus(
    userState?.roleInfo ? false : true
  );

  const navigate = useNavigate();

  const { state, pathname } = useLocation();
  const hasScheduled = useRef(false);
  const checkIntervalRef = useRef(null);

  const scheduleTokenRefresh = useCallback(async () => {
    if (hasScheduled.current) return;
    hasScheduled.current = true;
    const time_expiration = localStorage.getItem("time_expiration");
    const now = Date.now();
    const refreshTime = time_expiration - now - 60 * 1000;

    if (refreshTime <= 0) {
      signOut();
      return;
    }

    setTimeout(async () => {
      const resp = await fetch(`${public_urls}/auth/session/refresh`, { method: "POST", credentials: "include" });
      if (!resp.ok) {
        signOut();
        return;
      }
      const data = await resp.json();
      localStorage.setItem("time_expiration", data.expiration);
      //---------------------------------------------------
      hasScheduled.current = false;
      scheduleTokenRefresh();
    }, refreshTime);
  }, []);

  const checkSession = useCallback(async () => {
    if (checkIntervalRef.current) return;

    checkIntervalRef.current = setInterval(async () => {
      const resp = await fetch(`${public_urls}/auth/session/check`,
        {
          method: "POST",
          credentials: "include",
        });
      if (!resp.ok) {
        notifyError("Error verificando tu sesión.");
        setTimeout(() => { signOut(); }, 5000)
        return;
      }

      const data = await resp.json();
      if (data.force_logout) {
        const msg =
          data.reason ||
          "Tu sesión fue cerrada porque se inició sesión desde otro dispositivo.";
        notifyError(msg)
        setTimeout(() => { signOut() }, 5000)
      }
    }, 60_000);
  }, []);

  // const loadUserInfo = async () => {
  //   const resp = await fetch(`${public_urls}/auth/session/get-attributes`, {
  //     method: "POST",
  //     credentials: "include",
  //   });

  //   if (!resp.ok) {
  //     signOut();
  //     return;
  //   }

  //   const data = await resp.json();

  //   const loggedUser = {
  //     attributes: {
  //       email: data?.email,
  //       name: data?.name,
  //     },
  //   };

  //   const uInfo = {
  //     attributes: { ...loggedUser.attributes },
  //   };

  //   dispatchAuth({
  //     type: CONFIRM_SIGN_IN,
  //     payload: { loggedUser },
  //   });

  //   dispatchAuth({
  //     type: SET_USERINFO,
  //     payload: { uInfo },
  //   });

  //   scheduleTokenRefresh();
  //   checkSession();
  // };

  const loadUserInfo = async () => {
    try {
      const resp = await fetch(`${public_urls}/auth/session/get-attributes`, {
        method: "POST",
        credentials: "include",
      });

      if (!resp.ok) {
        return;
      }

      const data = await resp.json();

      const loggedUser = {
        attributes: {
          email: data?.email,
          name: data?.name,
        },
      };

      const uInfo = {
        attributes: { ...loggedUser.attributes },
      };

      dispatchAuth({
        type: CONFIRM_SIGN_IN,
        payload: { loggedUser },
      });

      dispatchAuth({
        type: SET_USERINFO,
        payload: { uInfo },
      });

      scheduleTokenRefresh();
      checkSession();
    } catch (err) {
      console.error(err);
    }
  };

  const confirmSignIn = useCallback(
    async () => {
      try {
        loadUserInfo();
        navigate(
          state?.from?.pathname
            ? state?.from?.pathname
            : pathname === "/login"
              ? "/"
              : pathname
        );
        setTimer((old) => {
          if (!old) {
            return old;
          }
          clearTimeout(old);
          return null;
        });
        setShowModalPublicidad(true);
      } catch (err) {
        if (err.code === "NotAuthorizedException") {
          dispatchAuth({ type: SIGN_OUT });
        }
        throw err;
      }
    },
    [navigate, state, pathname]
  );

  const signOut = useCallback(() => {
    console.log("SIGNOUT ******")
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    dispatchAuth({ type: SIGN_OUT });
    localStorage.clear()
    const target = `${process.env.REACT_APP_TARGET_TENANT}` || 'A';
    window.location.href = `${public_urls}/auth/logout?target=${target}`;
    // Cache_Remove_StatusUpdateDatetimeoldPeticion();
    // Cache_Remove_StatusUpdateDatetimeoldShowmodal();
  }, [navigate]);

  const [updateCommerceQuota] = useFetchDebounceECDH(
    {
      url: useMemo(
        () =>
          `${urlQuota}?id_comercio=${id_comercio}&id_dispositivo=${id_dispositivo}`,
        [id_comercio, id_dispositivo]
      ),
      autoDispatch: useMemo(() => pathname, [pathname]),
      // Se acplica `!!` en el fetchIf por lo que los valores
      // indefinidos(undefined) deben transformarse a bool.
      // Si recibe indefinido usara el valor por defecto
      fetchIf: useMemo(
        () => !!id_comercio && !!id_dispositivo,
        [id_comercio, id_dispositivo]
      ),
    },
    {
      onSuccess: useCallback((response) => {
        const quota = response?.obj || response
        const tempRole = {
          quota: 0,
          comision: 0,
          sobregiro: 0,
          sobregirovalue: 0,
          alerta: "",
          tipo_pago_comision: "",
        };
        tempRole.quota = quota["cupo disponible"];
        tempRole.comision = quota["comisiones"];
        tempRole.sobregiro = quota["dias sobregiro"] ?? 0;
        tempRole.sobregirovalue = quota["sobregiro"];
        tempRole.deuda = quota["cupo_canje"] ?? 0;
        tempRole.alerta = quota["alerta cupo"];
        tempRole.tipo_pago_comision = quota["tipo pago comision"] ?? "";
        dispatchAuth({ type: SET_QUOTA, payload: { quota: tempRole } });
      }, []),
      onError: useCallback((error) => {
        dispatchAuth({
          type: SET_QUOTA,
          payload: {
            quota: {
              quota: 0,
              comision: 0,
              sobregiro: 0,
              alerta: "",
              deuda: 0,
            },
          },
        });
        if (error?.cause === "custom") {
          notifyError(error.message);
        } else {
          console.error(error);
        }
      }, []),
    },
    {
      ivEncrypt: process.env.REACT_APP_IV_AES_ENCRYPT_COMERCIO,
      ivDecrypt: process.env.REACT_APP_IV_AES_DECRYPT_COMERCIO,
    }
  );
  useFetchDebounceECDH(
    {
      url: useMemo(
        () => `${urlLog}?correo=${userState?.userInfo?.attributes?.email}`,
        [userState?.userInfo?.attributes?.email]
      ),
      fetchIf: useMemo(
        () =>
          !!userState?.userInfo?.attributes?.email && !!pdpUser?.fk_id_comercio,
        [userState?.userInfo?.attributes?.email, pdpUser?.fk_id_comercio]
      ),
    },
    {
      onSuccess: useCallback(
        (response) => {
          const suserInfo = response?.obj || response;
          let _roleinfo = {};
          setSuserInactive((old) =>
            "msg" in suserInfo ? suserInfo?.msg : old
          );
          if (!("msg" in suserInfo)) {
            _roleinfo = structuredClone(suserInfo);
          }
          fetchDane(suserInfo.codigo_dane)
            .then((ciudad) => {
              _roleinfo.ciudad = ciudad;
            })
            .catch(() => { })
            .finally(() => {
              dispatchAuth({
                type: SET_ROLEINFO,
                payload: structuredClone(_roleinfo),
              });
            });
          if (pdpUser?.fk_id_comercio)
            eventStatusUpdate(pdpUser?.fk_id_comercio)
              .then((detail_status_update_) => {
                if (detail_status_update_)
                  dispatchAuth({
                    type: SET_DETAIL_STATUS_UPDATE,
                    payload: { detail_status_update: detail_status_update_ },
                  });
              })
              .catch(() => { });
        },
        [eventStatusUpdate, pdpUser?.fk_id_comercio]
      ),
      onError: useCallback(
        (error) => {
          if (error?.cause === "custom-403") {
            notifyError(error.message);
            signOut();
          } else if (error?.cause === "custom") {
            notifyError(error.message);
            setSuserInactive(error.message);
          } else {
            console.error(error);
          }
        },
        [signOut]
      ),
    },
    {
      ivEncrypt: process.env.REACT_APP_IV_AES_ENCRYPT_COMERCIO,
      ivDecrypt: process.env.REACT_APP_IV_AES_DECRYPT_COMERCIO,
    }
  );

  useFetchDebounceECDH(
    {
      url: useMemo(
        () =>
          `${url_iam_pdp_users}/user-login-v2?email=${userState?.userInfo?.attributes?.email}`,
        [userState?.userInfo?.attributes?.email]
      ),
      fetchIf: useMemo(
        () => !!userState?.userInfo?.attributes?.email,
        [userState?.userInfo?.attributes?.email]
      ),
    },
    {
      onPending: useCallback(() => {
        setNotLoadedPermissions(true);
      }, []),
      onSuccess: useCallback(
        (res) => {
          const pdpU = res?.obj?.pdpU;
          if (!pdpU && !pdpU.active) {
            notifyError("Usuario inactivo");
            signOut();
            return;
          }

          dispatchAuth({
            type: SET_PERMISSIONS,
            payload: { uAccess: res?.obj?.uAccess ?? [] },
          });
          dispatchAuth({ type: SET_PDPUSER, payload: { pdpU } });
        },
        [signOut]
      ),
      onError: useCallback(
        (error) => {
          if (error?.cause in ["custom-403", "custom"]) {
            notifyError(error.message);
            signOut();
          } else {
            console.error(error);
          }
        },
        [signOut]
      ),
      onFinally: useCallback(() => {
        setNotLoadedPermissions(false);
      }, []),
    },
    {
      ivEncrypt: process.env.REACT_APP_IV_AES_ENCRYPT_IAM,
      ivDecrypt: process.env.REACT_APP_IV_AES_DECRYPT_IAM,
    }
  );

  useFetchDebounceECDH(
    {
      url: useMemo(
        () =>
          `${url_pdp_commerce}/comercios/consultar-unique?pk_comercio=${id_comercio}`,
        [id_comercio]
      ),
      autoDispatch: useMemo(() => pathname, [pathname]),
      fetchIf: useMemo(() => !!id_comercio, [id_comercio]),
    },
    {
      onSuccess: useCallback((res) => {
        const commerce = res?.obj;
        dispatchAuth({ type: SET_COMMERCE_INFO, payload: { commerce } });
      }, []),
      onError: useCallback((error) => {
        if (error?.cause === "custom") {
          notifyError(error.message);
        } else {
          console.error(error);
        }
      }, []),
    },
    {
      ivEncrypt: process.env.REACT_APP_IV_AES_ENCRYPT_COMERCIO,
      ivDecrypt: process.env.REACT_APP_IV_AES_DECRYPT_COMERCIO,
    }
  );

  // // Runs only when route change
  useEffect(() => {
    loadUserInfo();        
  }, []);

  useEffect(() => {
    const isPdpCommerce = !!pdpUser?.fk_id_comercio;
    setSuserInactive((old) => {
      if (isPdpCommerce && old) {
        notifyError(old, false, { toastId: "failed-suser" });
        signOut();
      }
      return "";
    });
  }, [pdpUser?.fk_id_comercio, signOut]);

  useEffect(() => {
    setTimer((old) => {
      if (!userState?.isSignedIn || !old) {
        return old;
      }
      clearTimeout(old);
      return null;
    });
  }, [userState?.isSignedIn]);

  return {
    confirmSignIn,
    signOut,
    qr,
    timer,
    parameters,
    infoTicket,
    validateUser,
    updateCommerceQuota,
    ...userState,
    showModalPublicidad,
    setShowModalPublicidad,
    notLoadedPermissions,
  };
};
